import PropTypes from 'prop-types'
import { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react'

import { C, E, clamp, lerp, mixColor, prog, spring } from '../../clip-geometry'
import { BORDER, BX, BY, SH, SW, blink, lookDir } from '../stage'

// Variante "Logo": das Aivy-A aus seinen vier echten Logo-Teilen (src/assets/images/aivyicon.svg).
// Die Teile fliegen rein, rasten ein, das A bekommt Augen und huepft in den Button.

// Logo-Koordinaten 64 x 56, Anker unten mittig
const AX = 31.6
const AY = 55.3
const PIECES = [
  {
    c: '#FE79A7',
    d: 'M62.9024197,52.5405494 L33.2020455,1.59066988 C32.8307738,0.954428916 32.1747469,0.650814458 31.5255323,0.669031325 L31.5241699,0.669031325 C31.2189777,0.677127711 30.9165105,0.755392771 30.6453799,0.909224096 C30.6085934,0.92946506 30.5786192,0.961175904 30.5431951,0.984115663 C30.4471413,1.04753735 30.3504063,1.10893494 30.2659334,1.19259759 C30.1487615,1.30594699 30.0438517,1.43818795 29.9552914,1.59066988 L0.254235966,52.5405494 C0.0737093557,52.8495614 -0.00531361345,53.1788145 -0.000126887496,53.497947 C0.0171670588,54.4492723 0.786278543,55.3115373 1.85785725,55.3230072 C1.86466958,55.3230072 1.87011944,55.3250313 1.87693176,55.3250313 L1.87693176,55.3250313 L22.1708477,55.3250313 L31.5255323,39.2786699 L40.8795357,55.3250313 L61.2790427,55.3250313 C62.7218931,55.3250313 63.6238449,53.777947 62.9024197,52.5405494',
    tx: 0,
    ty: 0,
    cx: 31.6,
    cy: 28,
    from: [0, 0, 0],
    at: 0.05
  },
  {
    c: '#FFB629',
    d: 'M31.8765714,0.556086747 C19.2574207,0.556086747 8.13153165,6.9104 1.55286947,16.5774843 L0.702691317,18.0368578 L0.253759104,18.8053398 C-0.106612885,19.4247133 -0.0616515406,20.1210024 0.253759104,20.6621108 C0.275558543,20.6985446 0.307576471,20.7275566 0.330738375,20.762641 C0.395455462,20.8577735 0.458810084,20.9535807 0.539195518,21.035894 C0.582794398,21.0790747 0.63797423,21.1107855 0.685660504,21.1505928 C0.759914846,21.2119904 0.82940056,21.2787855 0.913192157,21.329388 C0.975865546,21.3664964 1.05011989,21.3867373 1.1175619,21.4164241 C1.1993098,21.4521831 1.27560784,21.4967133 1.36348683,21.5210024 C1.43842241,21.5419181 1.52221401,21.5419181 1.60123697,21.553388 C1.69320336,21.5668819 1.78040112,21.5904964 1.87713613,21.5904964 L22.1710521,21.5904964 L34.3787384,0.649195181 C33.5510409,0.59386988 32.7185748,0.556086747 31.8765714,0.556086747',
    tx: 0,
    ty: 33.73494,
    cx: 12,
    cy: 47,
    from: [-90, 70, -120],
    at: 0.3
  },
  {
    c: '#4264DC',
    d: 'M21.1239978,55.3253012 L41.5235048,55.3253012 C42.9609053,55.3253012 43.8587697,53.7910361 43.1530129,52.5556627 L13.4356078,1.57744578 C12.7080515,0.354216867 10.9177725,0.357590361 10.1990723,1.59026506 L-0.000340616246,19.0872289 L21.1239978,55.3253012 Z',
    tx: 19.755742,
    ty: 0,
    cx: 42,
    cy: 27,
    from: [100, -80, 140],
    at: 0.42
  },
  {
    c: '#00CEC8',
    d: 'M28.6954201,34.4234699 L40.8799444,55.3249639 L61.2794514,55.3249639 C62.7161708,55.3249639 63.6147164,53.7906988 62.9089596,52.556 L59.7977708,47.2184578 C53.0556128,39.3150361 42.9801842,34.2912289 31.7160049,34.2912289 C30.6975624,34.2912289 29.6927444,34.3431807 28.6954201,34.4234699',
    tx: 0,
    ty: 0,
    cx: 47,
    cy: 46,
    from: [100, 80, 100],
    at: 0.54
  }
]
const EYES = [
  [28, 25.5],
  [37, 25.5]
]
const BIG = { x: 176, y: 200, s: 1.75 }
const SMALL = { x: BX, y: BY + 16.5, s: 0.6 }
const INTRO_END = 2.7

// Sprung mit Landung, liefert Hoehe und Stauchung
function hop(t, t0, h) {
  const j = prog(t, t0, t0 + 0.42)
  if (t < t0) return { y: 0, sq: 1 }
  if (j < 1)
    return {
      y: h * Math.sin(Math.PI * j),
      sq: 1 + 0.08 * Math.sin(Math.PI * j)
    }
  const tau = t - t0 - 0.42
  return { y: 0, sq: 1 - 0.2 * Math.exp(-10 * tau) * Math.cos(15 * tau) }
}

function frame(t, ui, el) {
  const sh = E.inOutCubic(prog(t, 1.55, 2.1))
  const X = lerp(BIG.x, SMALL.x, sh)
  let Y = lerp(BIG.y, SMALL.y, sh)
  const s = lerp(BIG.s, SMALL.s, sh) * (1 + 0.06 * ui.hover)

  // Spruenge: einer am Ende des Intros, danach ab und zu
  let hp = hop(t, 2.15, 10)
  if (t > INTRO_END + 1.5) {
    const n = Math.floor((t - INTRO_END - 1.5) / 5.5)
    hp = hop(t, INTRO_END + 1.5 + n * 5.5, 5)
  }
  Y -= hp.y
  const idle = clamp((t - 2.1) * 2)
  const d = lookDir(ui)
  const tilt =
    2 * Math.sin(t * 1.3) * idle +
    clamp(ui.px / 120, -1, 1) * 7 * ui.near -
    4 * ui.open
  el.logo.setAttribute(
    'transform',
    `translate(${X.toFixed(2)} ${Y.toFixed(2)}) rotate(${tilt.toFixed(2)}) scale(${(s * (2 - hp.sq)).toFixed(4)} ${(s * hp.sq).toFixed(4)}) translate(${-AX} ${-AY})`
  )

  // Teile fliegen rein, Hover zeigt die Explosionszeichnung, Nachdenken pulsiert
  el.pieces.forEach((g, i) => {
    const p = PIECES[i]
    if (t < p.at) {
      g.style.opacity = 0
      return
    }
    g.style.opacity = 1
    const k = spring(t - p.at, 7, 11)
    let ox = p.from[0] * (1 - k)
    let oy = p.from[1] * (1 - k)
    const rot = p.from[2] * (1 - k)
    const dx = p.cx - AX
    const dy = p.cy - 30
    const dl = Math.hypot(dx, dy) || 1
    const ex =
      3.2 * ui.hover + 4 * ui.thinking * Math.max(0, Math.sin(t * 8 - i * 1.3))
    ox += (dx / dl) * ex
    oy += (dy / dl) * ex
    const sc = i === 0 ? spring(t - p.at, 8, 12) : 1
    g.setAttribute(
      'transform',
      `translate(${ox.toFixed(2)} ${oy.toFixed(2)}) rotate(${rot.toFixed(1)} ${p.cx} ${p.cy}) translate(${p.cx} ${p.cy}) scale(${sc.toFixed(3)}) translate(${-p.cx + p.tx} ${-p.cy + p.ty})`
    )
  })

  // Einrast-Ring
  const q = prog(t, 1.05, 1.5)
  const ringOn = q > 0 && q < 1
  el.ring.style.opacity = ringOn ? 1 : 0
  if (ringOn) {
    el.ring.setAttribute('cx', BIG.x)
    el.ring.setAttribute('cy', BIG.y - 48)
    el.ring.setAttribute('r', lerp(40, 120, E.outCubic(q)))
    el.ring.setAttribute('stroke-width', 5 * (1 - q))
  }

  // Augen
  const open = spring(t - 1.2, 9, 16) * blink(t, 4.4)
  let lk =
    ui.open > 0.5 ? [0, -1.6] : [d[0] * 1.3 * ui.near, d[1] * 1.3 * ui.near]
  lk = [lerp(lk[0], 1.2, ui.thinking), lerp(lk[1], -1.5, ui.thinking)]
  el.eyes.forEach((e, i) => {
    e.setAttribute(
      'transform',
      `translate(${EYES[i][0] + lk[0]} ${EYES[i][1] + lk[1]}) scale(1 ${Math.max(0, open).toFixed(3)})`
    )
  })

  // Mund: Laecheln, breiter bei Hover und offenem Chat, nachdenklich beim Denken
  const smile = Math.max(ui.hover, ui.open)
  const mw = lerp(lerp(2.6, 3.6, smile), 1.6, ui.thinking)
  const md = lerp(lerp(2.2, 3.4, smile), 0, ui.thinking)
  el.mouth.setAttribute(
    'd',
    `M${32.5 - mw} 31.6Q32.5 ${31.6 + md} ${32.5 + mw} 31.6`
  )
  el.mouth.style.opacity = t > 1.3 ? 1 : 0

  // Button-Kreis
  el.circle.setAttribute(
    'r',
    Math.max(0, 30 * spring(t - 1.9, 8, 14) * (1 + 0.05 * ui.hover))
  )
  el.circle.setAttribute('stroke', mixColor(BORDER, C.blue, ui.hover))
}

const LogoStage = forwardRef(function LogoStage(_, ref) {
  const svgRef = useRef(null)
  const elRef = useRef(null)

  useLayoutEffect(() => {
    const svg = svgRef.current
    elRef.current = {
      circle: svg.querySelector('.alg-circle'),
      ring: svg.querySelector('.alg-ring'),
      logo: svg.querySelector('.alg-logo'),
      pieces: Array.from(svg.querySelectorAll('.alg-piece')),
      eyes: Array.from(svg.querySelectorAll('.alg-eye')),
      mouth: svg.querySelector('.alg-mouth')
    }
  }, [])

  useImperativeHandle(ref, () => ({
    frame: (t, ui) => elRef.current && frame(t, ui, elRef.current)
  }))

  return (
    <svg
      ref={svgRef}
      width={SW}
      height={SH}
      viewBox={`0 0 ${SW} ${SH}`}
      className='absolute inset-0 overflow-visible'
      aria-hidden='true'
    >
      <circle
        className='alg-circle'
        cx={BX}
        cy={BY}
        r='0'
        fill='#fff'
        strokeWidth='1.5'
      />
      <circle
        className='alg-ring'
        r='0'
        fill='none'
        stroke={C.blue}
        opacity='0'
      />
      <g className='alg-logo'>
        {PIECES.map((p) => (
          <g key={p.c} className='alg-piece' opacity='0'>
            <path d={p.d} fill={p.c} />
          </g>
        ))}
        <path
          className='alg-mouth'
          fill='none'
          stroke='#fff'
          strokeWidth='1.4'
          strokeLinecap='round'
          opacity='0'
        />
        {EYES.map((e) => (
          <rect
            key={e[0]}
            className='alg-eye'
            x='-2.1'
            y='-3.8'
            width='4.2'
            height='7.6'
            rx='2.1'
            fill='#fff'
          />
        ))}
      </g>
    </svg>
  )
})

export function LogoAvatar({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox='0 0 100 100' aria-hidden='true'>
      <circle
        cx='50'
        cy='50'
        r='48.5'
        fill='#fff'
        stroke={BORDER}
        strokeWidth='3'
      />
      <g transform={`translate(50 52) scale(1.05) translate(${-AX} ${-28})`}>
        {PIECES.map((p) => (
          <path
            key={p.c}
            d={p.d}
            fill={p.c}
            transform={`translate(${p.tx} ${p.ty})`}
          />
        ))}
        <path
          d='M29.9 31.6Q32.5 33.8 35.1 31.6'
          fill='none'
          stroke='#fff'
          strokeWidth='1.4'
          strokeLinecap='round'
        />
        {EYES.map((e) => (
          <rect
            key={e[0]}
            x={e[0] - 2.1}
            y={e[1] - 3.8}
            width='4.2'
            height='7.6'
            rx='2.1'
            fill='#fff'
          />
        ))}
      </g>
    </svg>
  )
}

LogoAvatar.propTypes = { size: PropTypes.number }

export default {
  id: 'logo',
  name: 'Logo',
  description:
    'Das Aivy-A setzt sich aus seinen vier echten Logo-Teilen zusammen, rastet ein und bekommt Augen. Danach hüpft es in den Button, lehnt sich zur Maus und zeigt beim Hover seine Explosionszeichnung. Beim Nachdenken pulsieren die Teile.',
  introEnd: INTRO_END,
  Stage: LogoStage,
  Avatar: LogoAvatar
}
